import {I18n} from 'aws-amplify/utils';

import {DnsWriteErrorKind} from './types';

// FNP-1191 CRUD half — WHAT THE HUMAN IS TOLD, PER FAILURE CLASS.
//
// ⚠️ THE POINT OF THE WHOLE ERROR TAXONOMY LANDS HERE. Classifying
// ZONE_NOT_EMPTY separately from VALIDATION_REJECTED buys exactly one thing:
// different words. If every class rendered "something went wrong", the closed
// union upstream would be decoration.
//
// Each entry answers TWO questions, because a failure message that answers only
// the first is the thing operators complain about:
//   title  — what happened
//   action — what to do about it
//
// ⚠️ These are separate from the provider's own message, never a replacement
// for it. The provider text is rendered alongside, verbatim (FNP-1180:
// classify for the core, preserve for the human — both, always). FNP-1087 cost
// a whole investigation because only the normalised class survived.

type WriteErrorCopy = {
    title: string,
    action: string,
}

// Not a plain object literal at module scope: `I18n.get` must run at call time,
// after the language is configured, or every string is captured in the default
// locale at import.
export const writeErrorCopy = (kind: DnsWriteErrorKind): WriteErrorCopy => {

    switch (kind) {

        // ⚠️ THE REFUSAL THIS TICKET EXISTS TO SURFACE.
        //
        // The zone is serving something. Deleting it would take those hostnames
        // offline, which is why the provider refuses and why the refusal is
        // USEFUL rather than an obstacle. The copy names the actual next step —
        // "delete the records first" — because an operator who is told only
        // "invalid request" will retry the identical click.
        case 'ZONE_NOT_EMPTY':
            return {
                title: I18n.get('This zone still holds records'),
                action: I18n.get('The provider refused to delete it because it is still serving DNS records beyond the ones it created itself. Delete those records first, then delete the zone. Nothing has been changed.'),
            };

        case 'PERMISSION_DENIED':
            return {
                title: I18n.get('This connection is not allowed to do that'),
                action: I18n.get('The credential is valid but does not carry the permission for this action. Reconnect the provider with a credential that can write, in Settings.'),
            };

        case 'AUTHENTICATION_FAILED':
            return {
                title: I18n.get('The connection credential was rejected'),
                action: I18n.get('The token or role for this connection is expired or revoked. Reconnect the provider in Settings — retrying will not help.'),
            };

        case 'NOT_FOUND':
            return {
                title: I18n.get('That zone no longer exists at the provider'),
                action: I18n.get('It may already have been deleted, possibly from the provider\'s own console. Reload the list to see the current state.'),
            };

        case 'RATE_LIMITED':
            return {
                title: I18n.get('The provider is rate limiting requests'),
                action: I18n.get('This is temporary. Wait a moment and try again — nothing has been changed.'),
            };

        case 'PROVIDER_UNAVAILABLE':
            return {
                title: I18n.get('The provider could not be reached'),
                action: I18n.get('The provider is down or unreachable. This is temporary and retrying may work — nothing has been changed.'),
            };

        // A malformed request. NOT retryable: the same input stays invalid, so
        // the copy sends the operator to change something rather than to retry.
        case 'VALIDATION_REJECTED':
            return {
                title: I18n.get('The provider rejected this request'),
                action: I18n.get('The provider considers the request invalid — most often a malformed zone name. Retrying the same values will not help; check the message below and correct them.'),
            };

        // ⚠️ Claims NOTHING it has no evidence for. No "try again", because an
        // unclassified failure may well have partly succeeded, and no
        // reassurance that nothing changed.
        default:
            return {
                title: I18n.get('The request did not complete'),
                action: I18n.get('The provider did not return a recognisable reason. Reload the list to see the current state before trying again.'),
            };
    }
};

// Whether a second identical attempt could plausibly succeed WITHOUT a human
// changing something first.
//
// ⚠️ ZONE_NOT_EMPTY IS DELIBERATELY FALSE HERE. It is retryable only AFTER
// someone deletes the records — offering a Retry button on it sends an operator
// into a loop that cannot succeed, which is the same mistake the read half's
// failure banner avoids for revoked tokens.
export const isWriteErrorRetryable = (kind: DnsWriteErrorKind): boolean =>
    kind === 'RATE_LIMITED' || kind === 'PROVIDER_UNAVAILABLE';
